/**
 * PT-5628: the DGA index of the last API domain that earned trust, remembered across cold starts.
 *
 * Discovery probes a window of DGA candidates serially with a per-probe timeout (PT-5626), so a
 * cold start that begins at index 0 while the early candidates are blocked/sinkholed burns one
 * full timeout per dead domain before it reaches the live one — the exact censorship shape this
 * feature exists for. Persisting the index we last landed on lets the next boot resume the window
 * from there and skip those dead-domain timeouts.
 *
 * Home is localStorage, deliberately NOT the IndexedDB token store the ticket floated: this value
 * is read synchronously at boot to seed `discoverLiveApi`'s start index (no async hop before
 * discovery), it is discovery output that survives a reload exactly like the sibling
 * `vn-pwa:api-domain` key, and it carries no auth concern that would justify widening the token
 * store's contract. A blocked or missing read simply degrades to a full scan from 0 — same as a
 * first-ever visit — so the resume is a pure latency optimisation and never a correctness gate.
 */
export const LAST_KNOWN_INDEX_STORAGE_KEY = 'vn-pwa:api-index'

/**
 * Resolve `localStorage`, never throwing. In sandboxed in-app/WebView browsers even *reading*
 * `window.localStorage` can raise a SecurityError, and because default parameters are evaluated at
 * the call site before the callee's own try/catch runs, that read must be guarded here rather than
 * left as a bare default value.
 */
const resolveStorage = (): Pick<Storage, 'getItem' | 'setItem'> | undefined => {
  try {
    return localStorage
  } catch {
    return undefined
  }
}

/**
 * Remember the DGA index of the domain discovery just adopted, so the next cold start can resume
 * from it. Never throws — not even via its default parameter — because a blocked write is fully
 * recoverable (the next boot just falls back to a full scan) and must not tear down a successful
 * boot.
 */
export const persistApiIndex = (
  index: number,
  storage: Pick<Storage, 'setItem'> | undefined = resolveStorage(),
): void => {
  try {
    storage?.setItem(LAST_KNOWN_INDEX_STORAGE_KEY, String(index))
  } catch {
    // Storage blocked; the next boot's full scan from 0 is the fallback.
  }
}

/**
 * Read the last-known-good DGA index to resume discovery from, or `null` when there is nothing
 * trustworthy to resume from — no prior visit, a blocked read, or a corrupted value. Only a
 * non-negative integer is honoured; anything else is treated as absent so a garbled entry can
 * never seed discovery with a nonsense start index (which would silently skip the live window).
 */
export const loadApiIndex = (storage: Pick<Storage, 'getItem'> | undefined = resolveStorage()): null | number => {
  let raw: null | string | undefined
  try {
    raw = storage?.getItem(LAST_KNOWN_INDEX_STORAGE_KEY)
  } catch {
    return null
  }
  if (raw === null || raw === undefined) return null

  const parsed = Number(raw)
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null
}
